import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import type { Notification } from '@flemingmetrics/api-client';
import Badge from '@/components/Badge';
import NotificationsMenu from '@/menus/NotificationsMenu';
import { NotificationIcon } from '@/icons';
import NotificationToaster from './NotificationToaster';
import { useToggle } from '@/hooks/useToggle';
import {
  useAllNotifications,
  useCurrentUser,
  useDispatch,
  useUnreadNotificationsCount,
} from '@/hooks/useStore';
import { setNotifications as dispatchSetNotifications } from '@/store/slices/notificationsSlice';
import { setUnreadNotificationsCount as dispatchSetUnreadNotificationsCount } from '@/store/slices/unreadNotificationsCountSlice';
import { useAppState } from '@/hooks/useAppState';
import {
  classes,
  fetchNotifications,
  sortNotifications,
  styles,
} from './utils';

export type NotificationsProps = {
  className?: string;
};

export default function Notifications({ className }: NotificationsProps) {
  const unreadRef = useRef<number | null>(null);
  const menuToggleRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const currentUser = useCurrentUser();
  const notifications = useAllNotifications();
  const unreadCount = useUnreadNotificationsCount();
  const [activeNotification, setActiveNotification] =
    useState<Notification | null>(null);
  const [isMenuOpen, toggleMenu] = useToggle(false);

  useEffect(() => {
    if (currentUser) fetchData();
  }, [currentUser]);

  useEffect(() => {
    if (unreadCount !== null) checkNewNotifications();
  }, [unreadCount]);

  const fetchData = async () => {
    try {
      const data = await fetchNotifications();
      dispatch(dispatchSetNotifications(data.notifications));
      dispatch(dispatchSetUnreadNotificationsCount(data.unreadCount));
    } catch (err) {
      onApiError(err, 'Error fetching notifications', () => fetchData());
    }
  };

  const checkNewNotifications = () => {
    /*
      If new unread count is greater than the current one,
      show a toaster for the most recent notification
     */
    if (unreadRef.current === null) unreadRef.current = unreadCount!;
    if (unreadCount! > unreadRef.current) {
      const notification = sortNotifications(notifications)[0];
      if (notification) setActiveNotification(notification);
    }
    if (unreadCount !== unreadRef.current) unreadRef.current = unreadCount;
  };

  return (
    <>
      {isMenuOpen && (
        <NotificationsMenu
          open
          anchorTo={menuToggleRef.current}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          notifications={sortNotifications(notifications)}
          onClose={() => toggleMenu()}
        />
      )}
      {activeNotification && (
        <NotificationToaster
          notification={activeNotification}
          onClose={() => setActiveNotification(null)}
        />
      )}
      <div
        className={twMerge(classes.wrapper, className)}
        ref={menuToggleRef}
        onClick={() => toggleMenu()}>
        <Badge badgeContent={unreadCount}>
          <NotificationIcon
            size={18}
            className={classes.icon}
            style={unreadCount && unreadCount > 0 ? styles.unread : undefined}
          />
        </Badge>
      </div>
    </>
  );
}
